import { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router";
import { Sparkles, Loader2, LayoutDashboard, History as HistoryIcon, FileText, Lightbulb, AlertTriangle, ShieldCheck, MessageSquare, NotebookPen, BarChart2, ListChecks, Highlighter } from "lucide-react";
import PageMeta from "../../../../shared/ui/common/PageMeta";
import GroupedTabNav from "../../../../shared/ui/tabs/GroupedTabNav";
import { EmptyState, PageHeader, Surface } from "../../../../shared/ui/primitives";
import { useMotionSafe } from "../../../../hooks/useMotionSafe";
import Button from "../../../../shared/ui/button/Button";
import ConfirmDialog from "../../../../shared/ui/modal/ConfirmDialog";

const PANEL_ID = "patient-panel";
import PatientViewSessions from "../components/PatientViewSessions";
import { deletePatient, exportPatient, getPatient, PatientComplianceTab, PatientNotesTab, RecommendationsCard, PatientChatTab } from "../../../patient";
import type { Patient } from "../../../patient";
import PatientViewTherapeuticPlans from "../components/PatientViewTherapeuticPlans";
import PatientAgreementsTab from "../components/PatientAgreementsTab";
import { PatientHighlightsTab } from "../../../session";
import { useSubscription } from "../../../subscription";
import { useAiInsightsGate } from "../../../ai_insights";
import SubscriptionRestrictedState from "../../../../shared/ui/common/SubscriptionRestrictedState";
import PatientAlertsTab from "../components/PatientAlertsTab";
import PatientViewOverview from "../components/PatientViewOverview";
import PatientToolDock from "../components/PatientToolDock";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";

type TabId = 'overview' | 'therapeuticPlans' | 'sessions' | 'recommendations' | 'alerts' | 'dataCompliance' | 'aiChat' | 'patientNotes' | 'patientAgreements' | 'highlights';

// AI Insights tool surfaces — the whole group disappears without adoption.
const AI_TAB_IDS: TabId[] = ['recommendations', 'alerts', 'aiChat'];
// Core follow-up work — always available.
const FOLLOW_UP_TAB_IDS: TabId[] = ['patientAgreements', 'highlights', 'patientNotes', 'dataCompliance'];
const GROUPED_TAB_IDS: TabId[] = [...AI_TAB_IDS, ...FOLLOW_UP_TAB_IDS];

export default function Patient() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isPremium } = useSubscription();
  const { adopted: aiInsightsAdopted, loading: adoptionLoading } = useAiInsightsGate();
  const { reduced } = useMotionSafe();

  const [patient, setPatient] = useState<Patient | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [confirmingExport, setConfirmingExport] = useState(false);
  const [exporting, setExporting] = useState(false);
  // What the last export or delete came to, said on the page rather than in
  // a browser alert.
  const [notice, setNotice] = useState<{ tone: "success" | "critical"; text: string } | null>(null);

  // The tab lives in the address. Whatever it names is only honoured if it is
  // one of the tabs this therapist can see; see `activeTab` below.
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab");

  const setActiveTab = (tab: TabId) => {
    setNotice(null);
    // Replaced, not pushed, as on the list: Back leaves the patient rather
    // than stepping through every tab looked at.
    setSearchParams({ tab }, { replace: true });
  };

  const fetchPatientData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const [patientData] = await Promise.all([getPatient(id)]);
      setPatient(patientData || null);
    } catch (err) {
      console.error("Error fetching patient data:", err);
      setError(t("patient.errorLoading"));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    fetchPatientData();
  }, [fetchPatientData]);

  const handleExportData = async () => {
    if (!patient?.id) return;
    setExporting(true);
    setNotice(null);
    try {
      const data = await exportPatient(patient.id);
      const blob = new Blob([data], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `alchely-patient-${patient.first_name}-${patient.last_name}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setNotice({ tone: "success", text: t("patient.exportSuccess") });
    } catch (err) {
      console.error("Error exporting patient data:", err);
      setNotice({ tone: "critical", text: t("patient.exportError") });
    } finally {
      setExporting(false);
      setConfirmingExport(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    setNotice(null);
    try {
      setDeleting(true);
      await deletePatient(id);
      navigate("/patients");
    } catch (err) {
      console.error("Error deleting patient:", err);
      setNotice({ tone: "critical", text: t("patient.errorDeleting") });
    } finally {
      setDeleting(false);
      setConfirmingDelete(false);
    }
  };

  const tabs = [
    { id: 'overview', label: t("patient.tabs.overview"), icon: <LayoutDashboard className="w-4 h-4" /> },
    { id: 'sessions', label: t("patient.tabs.sessions"), icon: <HistoryIcon className="w-4 h-4" /> },
    { id: 'therapeuticPlans', label: t("patient.tabs.therapeuticPlans"), icon: <FileText className="w-4 h-4" /> },
    { 
      id: 'recommendations', 
      label: t("patient.tabs.recommendations"), 
      icon: <Lightbulb className="w-4 h-4" /> 
    },
    { id: 'alerts', label: t("patient.tabs.alerts"), icon: <AlertTriangle className="w-4 h-4" /> },
    { id: 'aiChat', label: t("patient.tabs.aiChat"), icon: <MessageSquare className="w-4 h-4" /> },
    { id: 'patientAgreements', label: t("patient.tabs.patientAgreements"), icon: <ListChecks className="w-4 h-4" /> },
    { id: 'highlights', label: t("patient.tabs.highlights", "Highlights"), icon: <Highlighter className="w-4 h-4" /> },
    { id: 'patientNotes', label: t("patient.tabs.patientNotes"), icon: <NotebookPen className="w-4 h-4" /> },
    { id: 'dataCompliance', label: t("patient.tabs.dataCompliance"), icon: <ShieldCheck className="w-4 h-4" /> }
  ].filter(tab => {
    if (!isPremium && (tab.id === 'therapeuticPlans' || tab.id === 'recommendations' || tab.id === 'alerts' || tab.id === 'aiChat' || tab.id === 'patientAgreements' || tab.id === 'highlights')) {
      return false;
    }
    // The AI Insights group (and its header) disappears without tool adoption.
    if (!adoptionLoading && !aiInsightsAdopted && AI_TAB_IDS.includes(tab.id as TabId)) {
      return false;
    }
    return true;
  });

  const mainTabs = tabs.filter(t => !GROUPED_TAB_IDS.includes(t.id as TabId));

  const menuGroups = [
    {
      id: 'aiInsights',
      label: t("patient.tabs.aiInsights"),
      icon: <Sparkles className="w-4 h-4" />,
      tabs: tabs.filter(tab => AI_TAB_IDS.includes(tab.id as TabId)),
    },
    {
      id: 'followUp',
      label: t("patient.tabs.followUp"),
      icon: <BarChart2 className="w-4 h-4" />,
      tabs: tabs.filter(tab => FOLLOW_UP_TAB_IDS.includes(tab.id as TabId)),
    },
  ].filter(group => group.tabs.length > 0);

  const PRE_GROUP_IDS: TabId[] = ['overview', 'sessions'];
  const preGroupTabs = mainTabs.filter(t => PRE_GROUP_IDS.includes(t.id as TabId));
  const postGroupTabs = mainTabs.filter(t => !PRE_GROUP_IDS.includes(t.id as TabId));

  // An unknown tab, or one this therapist's plan or tools do not show, opens
  // the overview. The address is left alone, so a gate that is still loading
  // lands on the asked-for tab once it resolves.
  const activeTab: TabId = tabs.some((tab) => tab.id === requestedTab)
    ? (requestedTab as TabId)
    : 'overview';
  const activeTabData = tabs.find(t => t.id === activeTab) || tabs[0];

  return (
    <div className="min-h-screen pb-20 md:pr-24">
      <PageMeta />
      {patient && (
        <PatientToolDock
          patientId={patient.id}
          subjectLabel={`${patient.first_name} ${patient.last_name}`.trim()}
        />
      )}

      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        {patient && !loading && !error && (
          <PageHeader
            className="mb-6"
            eyebrow={t("patient.pageTitle")}
            title={`${patient.first_name} ${patient.last_name}`.trim()}
          />
        )}
        {loading ? (
          <div className="flex h-96 items-center justify-center">
            <Loader2
              role="status"
              aria-label={t("common.loading")}
              className="size-8 animate-spin text-accent-text"
            />
          </div>
        ) : error ? (
          <Surface as="section" tone="critical" padding="lg">
            <div role="alert">
              <EmptyState
                icon={<AlertTriangle className="size-5" />}
                title={t("patient.errorTitle", "Could not load this patient")}
                description={error}
                action={
                  <Button variant="outline" onClick={() => void fetchPatientData()}>
                    {t("patient.retry")}
                  </Button>
                }
              />
            </div>
          </Surface>
        ) : !patient ? (
          <Surface as="section" padding="lg">
            <EmptyState
              icon={<AlertTriangle className="size-5" />}
              title={t("patient.notFound")}
            />
          </Surface>
        ) : (
          <div>
            <GroupedTabNav
              label={`${patient.first_name} ${patient.last_name}`.trim()}
              panelId={PANEL_ID}
              activeTab={activeTab}
              onChange={(id) => setActiveTab(id)}
              leadingTabs={preGroupTabs}
              groups={menuGroups}
              trailingTabs={postGroupTabs}
              layoutId="active-tab-pill"
            />

            {notice && (
              <p
                role={notice.tone === "critical" ? "alert" : "status"}
                className={`mb-4 rounded-lg p-3 text-sm ${
                  notice.tone === "critical"
                    ? "bg-status-critical-tint text-status-critical-on-tint"
                    : "bg-status-success-tint text-status-success-on-tint"
                }`}
              >
                {notice.text}
              </p>
            )}

            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                id={PANEL_ID}
                role="tabpanel"
                aria-label={activeTabData.label}
                initial={{ opacity: 0, y: reduced ? 0 : 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: reduced ? 0 : -6 }}
                transition={{ duration: reduced ? 0.12 : 0.2 }}
              >
                {/* Content Area */}
                <div className="space-y-8">
                  {/* Overview Tab (Consolidated) */}
                  {activeTab === 'overview' && (
                    <PatientViewOverview
                      patient={patient}
                      onRefresh={fetchPatientData}
                      onTabChange={(tab) => setActiveTab(tab as TabId)}
                      onDelete={() => setConfirmingDelete(true)}
                      deleting={deleting}
                    />
                  )}

                  {/* Recommendations Tab */}
                  {activeTab === 'recommendations' && (
                    <div className="w-full">
                      <RecommendationsCard patientId={patient.id!} />
                    </div>
                  )}

                  {/* Alerts Tab */}
                  {activeTab === 'alerts' && isPremium && (
                    <div className="w-full">
                      <PatientAlertsTab patientID={patient.id!} />
                    </div>
                  )}

                  {/* Therapeutic Plans */}
                  {activeTab === 'therapeuticPlans' && (
                    <div className="w-full">
                      {!isPremium ? (
                        <SubscriptionRestrictedState
                          title={t("therapeuticPlan.restricted.title")}
                          description={t("therapeuticPlan.restricted.description")}
                        />
                      ) : (
                        id && <PatientViewTherapeuticPlans patientID={id} />
                      )}
                    </div>
                  )}

                  {/* Sessions */}
                  {activeTab === 'sessions' && (
                    <div className="w-full">
                      {id && <PatientViewSessions patientID={id} />}
                    </div>
                  )}

                  {/* AI Chat */}
                  {activeTab === 'aiChat' && isPremium && (
                    <div className="w-full">
                      <PatientChatTab patient={patient} persist={true} />
                    </div>
                  )}

                  {/* Patient Agreements */}
                  {activeTab === 'patientAgreements' && patient?.id && (
                    <div className="w-full">
                      <PatientAgreementsTab patientId={patient.id} />
                    </div>
                  )}

                  {/* Highlights */}
                  {activeTab === 'highlights' && patient?.id && (
                    <div className="w-full">
                      <PatientHighlightsTab patientId={patient.id} />
                    </div>
                  )}

                  {/* Patient Notes */}
                  {activeTab === 'patientNotes' && patient?.id && (
                    <div className="w-full">
                      <PatientNotesTab patientId={patient.id} patientName={`${patient.first_name} ${patient.last_name}`} />
                    </div>
                  )}

                  {/* Compliance */}
                  {activeTab === 'dataCompliance' && patient && (
                    <div className="w-full">
                      <PatientComplianceTab patient={patient} onExport={() => setConfirmingExport(true)} />
                    </div>
                  )}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={confirmingDelete}
        title={t("patient.deletePatient")}
        message={t("patient.confirmDelete")}
        confirmLabel={t("common.delete")}
        destructive
        isBusy={deleting}
        onConfirm={() => void handleDelete()}
        onCancel={() => {
          if (!deleting) setConfirmingDelete(false);
        }}
      />
      <ConfirmDialog
        isOpen={confirmingExport}
        title={t("patient.exportData")}
        message={t("patient.exportConfirm")}
        isBusy={exporting}
        onConfirm={() => void handleExportData()}
        onCancel={() => {
          if (!exporting) setConfirmingExport(false);
        }}
      />
    </div>
  );
}
